import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  ChevronDownIcon,
  ChevronUpIcon,
  Dropdown,
  OptionKey,
  Preloader,
  SearchInput,
  Text,
} from '@common-fe/common-fe';

import { useReplenishmentFilterItems } from '../../../Statistics/ReplenishmentStatistic/ReplenishmentFilterDropdown/hooks';
import { BannerReplenishmentFilterDropdownProps } from '../BannerReplenishmentPreview.types';

import {
  StyledDropdownButton,
  StyledDropdownList,
  StyledDropdownPanel,
} from './BannerReplenishmentPreview.styles';

/** Gap between the trigger and the portalled panel. */
const PANEL_OFFSET = 4;
/** The design draws the dropdown at 220px, the trigger can be narrower. */
const MIN_PANEL_WIDTH = 220;

interface PanelPosition {
  top: number;
  left: number;
  width: number;
}

export const BannerReplenishmentFilterDropdown: React.FC<BannerReplenishmentFilterDropdownProps> = ({
  planIds = [],
  isLoading: isPlanLoading,
  planValue,
  onSetPlanValue,
  subsidiaryValue,
  onSetSubsidiaryValue,
}) => {
  const [searchString, setSearchString] = useState('');
  const [isExpanded, setExpanded] = useState(false);
  const [position, setPosition] = useState<PanelPosition>({ top: 0, left: 0, width: 0 });

  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const { isLoading, planOptions, defaultTitle, subsidiariesOptions } = useReplenishmentFilterItems({
    searchString,
    planIds,
    isPlanLoading,
  });

  const updatePosition = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) {
      return;
    }
    setPosition({
      top: rect.bottom + PANEL_OFFSET,
      left: rect.left,
      width: Math.max(rect.width, MIN_PANEL_WIDTH),
    });
  }, []);

  useEffect(() => {
    if (!isExpanded) {
      return undefined;
    }
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isExpanded, updatePosition]);

  // The panel lives outside the trigger subtree, so both refs are checked here.
  useEffect(() => {
    if (!isExpanded) {
      return undefined;
    }
    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) {
        return;
      }
      setExpanded(false);
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isExpanded]);

  const handlePickPlanItem = useCallback(
    (newValue: OptionKey) => () => {
      onSetSubsidiaryValue('');
      onSetPlanValue(newValue);
      setExpanded(false);
      setSearchString('');
    },
    [onSetPlanValue, onSetSubsidiaryValue]
  );

  const handlePickSubsidiaryItem = useCallback(
    (newValue: OptionKey) => () => {
      onSetSubsidiaryValue(newValue);
      onSetPlanValue('');
      setExpanded(false);
    },
    [onSetPlanValue, onSetSubsidiaryValue]
  );

  const handleToggle = useCallback(() => {
    setExpanded(!isExpanded);
  }, [isExpanded]);

  const currentTitle = useMemo(() => {
    if (planValue) {
      const foundItem = planOptions.find((item) => item.key === planValue);
      if (foundItem?.value) {
        return foundItem.value as string;
      }
    }
    if (subsidiaryValue) {
      const foundItem = subsidiariesOptions.find((item) => item.key === subsidiaryValue);
      if (foundItem?.value) {
        return foundItem.value as string;
      }
    }
    return defaultTitle;
  }, [defaultTitle, planOptions, planValue, subsidiariesOptions, subsidiaryValue]);

  const isEmptyList = useMemo(
    () => !planOptions.length && !subsidiariesOptions.length,
    [planOptions.length, subsidiariesOptions.length]
  );

  const isEmpty = useMemo(() => isEmptyList && !searchString, [isEmptyList, searchString]);

  // With nothing to pick from the current organization name is shown as plain text.
  if (isEmpty && !isLoading) {
    return (
      <Box
        data-testid="dashboard_banner_replenishment-preview_plan-dropdown"
        direction="row"
        justify="start"
        style={{ flex: 1 }}
      >
        <Text
          data-testid="dashboard_banner_replenishment-preview_plan-dropdown_title"
          title={currentTitle}
          color="textOnColor"
          size="medium"
          weight={400}
          truncate
        >
          {currentTitle}
        </Text>
      </Box>
    );
  }

  return (
    <Box
      ref={triggerRef}
      data-testid="dashboard_banner_replenishment-preview_plan-dropdown"
      style={{ flex: 1 }}
    >
      <StyledDropdownButton
        data-testid="dashboard_banner_replenishment-preview_plan-dropdown_button"
        isExpanded={isExpanded}
        onClick={handleToggle}
      >
        <Box direction="row" align="center" justify="start">
          <Text
            data-testid="dashboard_banner_replenishment-preview_plan-dropdown_title"
            title={currentTitle}
            color="textOnColor"
            size="medium"
            weight={400}
            truncate
          >
            {currentTitle}
          </Text>
          {isExpanded ? (
            <ChevronUpIcon
              data-testid="dashboard_banner_replenishment-preview_plan-dropdown_chevron-up"
              size="24px"
              color="iconOnColor"
            />
          ) : (
            <ChevronDownIcon
              data-testid="dashboard_banner_replenishment-preview_plan-dropdown_chevron-down"
              size="24px"
              color="iconOnColor"
            />
          )}
        </Box>
      </StyledDropdownButton>

      {isExpanded &&
        createPortal(
          <StyledDropdownPanel
            ref={panelRef}
            data-testid="dashboard_banner_replenishment-preview_plan-dropdown_panel"
            background="canvas"
            round="container2Round"
            elevation="active"
            height={{ max: '282px' }}
            pad="spacing12"
            style={{ top: position.top, left: position.left, width: position.width }}
          >
            <SearchInput
              testId="dashboard_banner_replenishment-preview_plan-dropdown_search"
              value={searchString}
              onChange={setSearchString}
            />

            <StyledDropdownList pad={{ top: 'spacing8' }}>
              <Dropdown.Item
                testId="dashboard_banner_replenishment-preview_plan-dropdown_option-employer-level"
                isActive={planValue === '' && subsidiaryValue === ''}
                onClick={handlePickPlanItem('')}
                truncate
                title={defaultTitle || ''}
              >
                {defaultTitle || 'Employer level'}
              </Dropdown.Item>

              {isLoading || isPlanLoading ? (
                // Preloader does not forward data attributes, so the test id lives on a wrapper.
                <Box data-testid="dashboard_banner_replenishment-preview_plan-dropdown_loader">
                  <Preloader />
                </Box>
              ) : (
                <>
                  {!!subsidiariesOptions.length && (
                    <>
                      <Box margin={{ vertical: 'spacing4' }} height="1px" background="border2" flex={false} />
                      <Text size="small" color="textSecondary">
                        Subsidiaries
                      </Text>
                    </>
                  )}

                  {subsidiariesOptions.map((item) => (
                    <Dropdown.Item
                      key={item.key}
                      testId={`dashboard_banner_replenishment-preview_plan-dropdown_subsidiary-${item.key}`}
                      isActive={subsidiaryValue === item.key}
                      onClick={handlePickSubsidiaryItem(item.key)}
                      truncate
                      title={item.value as string}
                    >
                      {item.value as string}
                    </Dropdown.Item>
                  ))}

                  {!!planOptions.length && (
                    <Box margin={{ vertical: 'spacing4' }} height="1px" background="border2" flex={false} />
                  )}

                  {planOptions.map((item) => (
                    <Dropdown.Item
                      key={item.key}
                      testId={`dashboard_banner_replenishment-preview_plan-dropdown_plan-${item.key}`}
                      isActive={planValue === item.key}
                      onClick={handlePickPlanItem(item.key)}
                      title={item.value as string}
                      truncate
                    >
                      {item.value as string}
                    </Dropdown.Item>
                  ))}

                  {!!isEmptyList && (
                    <Text
                      data-testid="dashboard_banner_replenishment-preview_plan-dropdown_empty"
                      textAlign="center"
                      margin={{ top: 'spacing12', horizontal: 'spacing8' }}
                      color="textDisabled"
                    >
                      Not Found
                    </Text>
                  )}
                </>
              )}
            </StyledDropdownList>
          </StyledDropdownPanel>,
          document.body
        )}
    </Box>
  );
};

export default BannerReplenishmentFilterDropdown;
